/**
 * Enhanced Toast Component
 * Advanced toast notifications with actions, progress indicators, and accessibility
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  Loader2, 
  X, 
  RotateCcw, 
  ExternalLink,
  Phone,
  Eye,
  Clock
} from 'lucide-react';
import { TOAST_TYPES } from '../../contexts/EnhancedToastContext';

const EnhancedToast = ({
  id,
  message,
  type = TOAST_TYPES.INFO,
  title,
  description,
  duration = 5000,
  dismissible = true,
  showProgress = true,
  showIcon = true,
  showActions = false,
  actions = [],
  data = {},
  persistent = false,
  onClose,
  onAction
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);

  // Animation and auto-dismiss logic
  useEffect(() => {
    // Trigger entrance animation
    const showTimer = setTimeout(() => setIsVisible(true), 50);
    
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (persistent || duration === 0) return;

    let progressTimer;
    let dismissTimer;

    const startProgress = () => {
      if (isPaused) return;

      const interval = 50; // Update every 50ms
      const decrement = (interval / duration) * 100;

      progressTimer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            handleClose();
            return 0;
          }
          return newProgress;
        });
      }, interval);
    };

    if (!isPaused) {
      startProgress();
    }

    return () => {
      if (progressTimer) clearInterval(progressTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, [duration, persistent, isPaused, onClose]);

  const handleClose = useCallback(() => {
    if (!dismissible && !persistent) return;
    
    setIsExiting(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 200); // Match exit animation duration
  }, [dismissible, persistent, onClose]);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleAction = useCallback((actionType, actionData = {}) => {
    if (onAction) {
      onAction(actionType, actionData);
    }
  }, [onAction]);

  // Get toast styling based on type
  const getToastStyles = () => {
    const baseStyles = 'relative overflow-hidden rounded-lg shadow-lg border min-w-80 max-w-md';
    
    const typeStyles = {
      [TOAST_TYPES.SUCCESS]: 'bg-green-50 border-green-200 text-green-900',
      [TOAST_TYPES.ERROR]: 'bg-red-50 border-red-200 text-red-900',
      [TOAST_TYPES.WARNING]: 'bg-yellow-50 border-yellow-200 text-yellow-900',
      [TOAST_TYPES.INFO]: 'bg-blue-50 border-blue-200 text-blue-900',
      [TOAST_TYPES.LOADING]: 'bg-gray-50 border-gray-200 text-gray-900',
      [TOAST_TYPES.ASSIGNMENT_SUCCESS]: 'bg-green-50 border-green-200 text-green-900',
      [TOAST_TYPES.ASSIGNMENT_ERROR]: 'bg-red-50 border-red-200 text-red-900',
      [TOAST_TYPES.NETWORK_ERROR]: 'bg-orange-50 border-orange-200 text-orange-900'
    };
    
    return `${baseStyles} ${typeStyles[type] || typeStyles[TOAST_TYPES.INFO]}`;
  };

  // Get icon based on toast type
  const getIcon = () => {
    if (!showIcon) return null;
    
    const iconProps = { className: 'h-5 w-5 flex-shrink-0' };
    
    const icons = {
      [TOAST_TYPES.SUCCESS]: <CheckCircle {...iconProps} className={`${iconProps.className} text-green-600`} />,
      [TOAST_TYPES.ERROR]: <XCircle {...iconProps} className={`${iconProps.className} text-red-600`} />,
      [TOAST_TYPES.WARNING]: <AlertTriangle {...iconProps} className={`${iconProps.className} text-yellow-600`} />,
      [TOAST_TYPES.INFO]: <Info {...iconProps} className={`${iconProps.className} text-blue-600`} />,
      [TOAST_TYPES.LOADING]: <Loader2 {...iconProps} className={`${iconProps.className} text-gray-600 animate-spin`} />,
      [TOAST_TYPES.ASSIGNMENT_SUCCESS]: <CheckCircle {...iconProps} className={`${iconProps.className} text-green-600`} />,
      [TOAST_TYPES.ASSIGNMENT_ERROR]: <XCircle {...iconProps} className={`${iconProps.className} text-red-600`} />,
      [TOAST_TYPES.NETWORK_ERROR]: <AlertTriangle {...iconProps} className={`${iconProps.className} text-orange-600`} />
    };
    
    return icons[type] || icons[TOAST_TYPES.INFO];
  };

  // Get progress bar color
  const getProgressColor = () => {
    const colors = {
      [TOAST_TYPES.SUCCESS]: 'bg-green-500',
      [TOAST_TYPES.ERROR]: 'bg-red-500',
      [TOAST_TYPES.WARNING]: 'bg-yellow-500',
      [TOAST_TYPES.INFO]: 'bg-blue-500',
      [TOAST_TYPES.LOADING]: 'bg-gray-500',
      [TOAST_TYPES.ASSIGNMENT_SUCCESS]: 'bg-green-500',
      [TOAST_TYPES.ASSIGNMENT_ERROR]: 'bg-red-500',
      [TOAST_TYPES.NETWORK_ERROR]: 'bg-orange-500'
    };
    
    return colors[type] || colors[TOAST_TYPES.INFO];
  };

  // Render action buttons
  const renderActions = () => {
    if (!showActions || actions.length === 0) return null;
    
    const getActionIcon = (actionType) => {
      const iconProps = { className: 'h-4 w-4' };
      
      switch (actionType) {
        case 'retry':
          return <RotateCcw {...iconProps} />;
        case 'view_details':
          return <Eye {...iconProps} />;
        case 'contact_support':
          return <Phone {...iconProps} />;
        case 'refresh':
          return <ExternalLink {...iconProps} />;
        default:
          return null;
      }
    };

    return (
      <div className="flex items-center space-x-2 mt-3">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleAction(action.type, action.data)}
            className="inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded bg-white/50 hover:bg-white/70 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            disabled={action.delay && Date.now() < (data.timestamp || 0) + action.delay}
          >
            {getActionIcon(action.type)}
            <span>{action.label}</span>
            {action.delay && Date.now() < (data.timestamp || 0) + action.delay && (
              <Clock className="h-3 w-3 ml-1" />
            )}
          </button>
        ))}
      </div>
    );
  };

  // Animation classes
  const getAnimationClasses = () => {
    const enterClasses = isVisible 
      ? 'translate-x-0 opacity-100 scale-100' 
      : 'translate-x-full opacity-0 scale-95';
    
    const exitClasses = isExiting 
      ? 'translate-x-full opacity-0 scale-95' 
      : '';
    
    return `transform transition-all duration-200 ease-out ${enterClasses} ${exitClasses}`;
  };

  return (
    <div
      className={`${getToastStyles()} ${getAnimationClasses()} pointer-events-auto`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live={type === TOAST_TYPES.ERROR ? 'assertive' : 'polite'}
      aria-describedby={`toast-${id}-message`}
    >
      {/* Progress bar */}
      {showProgress && duration > 0 && !persistent && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-black/10">
          <div 
            className={`h-full transition-all duration-100 ease-linear ${getProgressColor()}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      
      <div className="p-4">
        <div className="flex items-start space-x-3">
          {getIcon()}
          
          <div className="flex-1 min-w-0">
            {title && (
              <h4 className="text-sm font-semibold mb-1">
                {title}
              </h4>
            )}
            
            <p id={`toast-${id}-message`} className="text-sm">
              {message}
            </p>
            
            {description && (
              <p className="text-xs mt-1 opacity-75">
                {description}
              </p>
            )}
            
            {renderActions()}
          </div>
          
          {dismissible && (
            <button
              onClick={handleClose}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedToast;